/**
 * EditTeamModal
 *
 * A modal for editing team details and configuring permissions.
 * Uses tabs to organize different sections with consistent height and scrolling.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Spinner } from '@/components/ui/spinner';
import { PermissionMatrix } from '@/components/permissions/PermissionMatrix';
import GroupDTO from 'src/types/user-management/GroupDTO';
import useOrganizationGroupDetails from 'src/api/organization/useOrganizationGroupDetails';
import useOrganizationMembers from 'src/api/organization/useOrganizationMembers';
import { useEditGroup } from 'src/api/organization/useEditGroup';
import { useAddUserToGroup } from 'src/api/organization/useAddUserToGroup';
import config from 'src/config';
import {
  UsersIcon,
  ShieldIcon,
  InfoIcon,
  FloppyDiskIcon,
  CircleNotchIcon,
  ArrowCounterClockwiseIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  UserMinusIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface Props {
  team: GroupDTO;
  onClose: (refetch?: boolean) => void;
}

const MAX_NAME_LENGTH = 255;
const MAX_DESCRIPTION_LENGTH = 255;
const MIN_DESCRIPTION_LENGTH = 10;

export function EditTeamModal({ team, onClose }: Props) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('permissions');
  const [teamName, setTeamName] = useState(team.name);
  const [teamDescription, setTeamDescription] = useState(team.description);
  const [memberSearch, setMemberSearch] = useState('');
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [addingMemberId, setAddingMemberId] = useState<string | null>(null);

  // Track permission footer state for rendering outside scroll container
  const [permissionFooterState, setPermissionFooterState] = useState({
    hasChanges: false,
    isSaving: false,
    enabledCount: 0,
  });
  const permissionActionsRef = useRef<{
    onSave: () => void;
    onReset: () => void;
  }>({ onSave: () => {}, onReset: () => {} });

  // API hooks
  const editGroupMutation = useEditGroup(team.id);
  const addMemberMutation = useAddUserToGroup(team.id);

  // Fetch group details with current members
  const {
    data: groupDetailsData,
    isLoading: groupDetailsLoading,
    refetch: refetchGroupDetails,
  } = useOrganizationGroupDetails(team.id);

  // Fetch all organization members
  const { data: allMembersData, isLoading: allMembersLoading } =
    useOrganizationMembers();

  // Get the latest group data (from query or fallback to prop)
  const groupData = groupDetailsData?.group;
  const displayName = groupData?.name ?? team.name;
  const displayDescription = groupData?.description ?? team.description;
  const displayMemberCount = groupData?.userCount ?? team.userCount;

  // Get current team members from group details
  const currentMembers = useMemo(
    () => groupData?.users ?? [],
    [groupData?.users],
  );
  const currentMemberIds = useMemo(
    () => new Set(currentMembers.map((m) => m.id)),
    [currentMembers],
  );

  // Sync form values when fetched data changes (after save/refetch)
  useEffect(() => {
    if (groupData) {
      setTeamName(groupData.name);
      setTeamDescription(groupData.description);
    }
  }, [groupData]);

  // Filter available members (not already in team) by search
  const availableMembers = useMemo(() => {
    if (!allMembersData?.members) return [];
    const search = memberSearch.toLowerCase();
    return allMembersData.members
      .filter((m) => !currentMemberIds.has(m.userId))
      .filter(
        (m) =>
          m.name.toLowerCase().includes(search) ||
          m.email.toLowerCase().includes(search),
      );
  }, [allMembersData?.members, currentMemberIds, memberSearch]);

  // Validation
  const nameLength = teamName.trim().length;
  const descriptionLength = teamDescription.trim().length;
  const isNameValid = nameLength > 0 && nameLength <= MAX_NAME_LENGTH;
  const isDescriptionValid =
    descriptionLength >= MIN_DESCRIPTION_LENGTH &&
    descriptionLength <= MAX_DESCRIPTION_LENGTH;
  const isDetailsValid = isNameValid && isDescriptionValid;

  // Track if details have been modified (compare to current fetched data)
  const detailsModified =
    teamName !== displayName || teamDescription !== displayDescription;
  const isSavingDetails = editGroupMutation.isPending;

  function handleSaveDetails() {
    const newName = teamName.trim();
    const newDescription = teamDescription.trim();
    editGroupMutation.mutate(
      { name: newName, description: newDescription },
      {
        onSuccess: () => {
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['organization/groups'] });
          refetchGroupDetails();
        },
      },
    );
  }

  function handleResetDetails() {
    setTeamName(displayName);
    setTeamDescription(displayDescription);
  }

  function handleAddMember(userId: string) {
    setAddingMemberId(userId);
    addMemberMutation.mutate(
      { userId },
      {
        onSuccess: () => {
          refetchGroupDetails();
          queryClient.invalidateQueries({ queryKey: ['organization/groups'] });
          setAddingMemberId(null);
        },
        onError: () => {
          setAddingMemberId(null);
        },
      },
    );
  }

  async function handleRemoveMember(userId: string) {
    setRemovingMemberId(userId);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/organization/groups/${team.id}/members/${userId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );
      if (response.ok) {
        refetchGroupDetails();
        queryClient.invalidateQueries({ queryKey: ['organization/groups'] });
      }
    } finally {
      setRemovingMemberId(null);
    }
  }

  return (
    <Dialog open={true} onOpenChange={() => onClose(true)}>
      <DialogContent className="sm:max-w-[90vw] w-[1400px] h-[85vh] flex flex-col p-0 gap-0">
        {/* Header - fixed height */}
        <div className="shrink-0 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-b">
          <DialogHeader className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg">
                <UsersIcon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-xl font-semibold truncate">
                    {displayName}
                  </DialogTitle>
                  <Badge variant="secondary" className="shrink-0">
                    {displayMemberCount} member
                    {displayMemberCount !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <DialogDescription className="text-muted-foreground truncate">
                  {displayDescription ||
                    'Configure team settings and permissions'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Tabs - flex-1 to fill remaining space */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col min-h-0"
        >
          {/* Tab list */}
          <div className="shrink-0 px-5 pt-1">
            <TabsList className="inline-flex h-9 items-center justify-start gap-1 rounded-lg bg-muted p-1">
              <TabsTrigger
                value="permissions"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                <ShieldIcon className="h-3.5 w-3.5" />
                Permissions
              </TabsTrigger>
              <TabsTrigger
                value="details"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                <InfoIcon className="h-3.5 w-3.5" />
                Details
              </TabsTrigger>
              <TabsTrigger
                value="members"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                <UsersIcon className="h-3.5 w-3.5" />
                Members
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab content - scrollable area that takes remaining space */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {/* Permissions Tab */}
            <TabsContent
              value="permissions"
              className="h-full m-0 flex flex-col"
            >
              <div className="flex-1 min-h-0 overflow-y-auto p-6">
                <PermissionMatrix
                  groupId={team.id}
                  groupName={team.name}
                  hideHeader
                  renderFooter={({
                    hasChanges,
                    isSaving,
                    onSave,
                    onReset,
                    enabledCount,
                  }) => {
                    // Store callbacks in ref (always update, no render needed)
                    permissionActionsRef.current = { onSave, onReset };

                    // Update state only when values change
                    if (
                      permissionFooterState.hasChanges !== hasChanges ||
                      permissionFooterState.isSaving !== isSaving ||
                      permissionFooterState.enabledCount !== enabledCount
                    ) {
                      // Use setTimeout to avoid setState during render
                      setTimeout(() => {
                        setPermissionFooterState({
                          hasChanges,
                          isSaving,
                          enabledCount,
                        });
                      }, 0);
                    }
                    return null; // Footer is rendered outside scroll container
                  }}
                />
              </div>
              {/* Footer bar - fixed at bottom, outside scroll area */}
              <div className="shrink-0 border-t bg-background/95 backdrop-blur-sm px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Badge variant="secondary">
                      {permissionFooterState.enabledCount}
                    </Badge>
                    permission
                    {permissionFooterState.enabledCount !== 1 ? 's' : ''}{' '}
                    enabled
                    {permissionFooterState.hasChanges && (
                      <span className="text-amber-600 dark:text-amber-400 ml-2">
                        • Unsaved changes
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => permissionActionsRef.current.onReset()}
                      disabled={
                        !permissionFooterState.hasChanges ||
                        permissionFooterState.isSaving
                      }
                    >
                      <ArrowCounterClockwiseIcon className="h-3.5 w-3.5 mr-1.5" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => permissionActionsRef.current.onSave()}
                      disabled={
                        !permissionFooterState.hasChanges ||
                        permissionFooterState.isSaving
                      }
                    >
                      {permissionFooterState.isSaving ? (
                        <>
                          <CircleNotchIcon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FloppyDiskIcon className="h-3.5 w-3.5 mr-1.5" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Details Tab */}
            <TabsContent value="details" className="h-full m-0 flex flex-col">
              <div className="flex-1 min-h-0 overflow-y-auto p-6">
                <div className="max-w-lg space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="team-name"
                        className="text-sm font-medium"
                      >
                        Team Name
                      </Label>
                      <span
                        className={cn(
                          'text-xs',
                          nameLength > MAX_NAME_LENGTH
                            ? 'text-destructive'
                            : 'text-muted-foreground',
                        )}
                      >
                        {nameLength}/{MAX_NAME_LENGTH}
                      </span>
                    </div>
                    <Input
                      id="team-name"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder="Enter team name"
                      maxLength={MAX_NAME_LENGTH}
                      className={cn(
                        nameLength > 0 && !isNameValid && 'border-destructive',
                      )}
                    />
                    {nameLength > MAX_NAME_LENGTH && (
                      <p className="text-xs text-destructive">
                        Name must be {MAX_NAME_LENGTH} characters or less
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="team-description"
                        className="text-sm font-medium"
                      >
                        Description
                      </Label>
                      <span
                        className={cn(
                          'text-xs',
                          descriptionLength > MAX_DESCRIPTION_LENGTH ||
                            (descriptionLength > 0 &&
                              descriptionLength < MIN_DESCRIPTION_LENGTH)
                            ? 'text-destructive'
                            : 'text-muted-foreground',
                        )}
                      >
                        {descriptionLength}/{MAX_DESCRIPTION_LENGTH}
                      </span>
                    </div>
                    <Textarea
                      id="team-description"
                      value={teamDescription}
                      onChange={(e) => setTeamDescription(e.target.value)}
                      placeholder="Describe this team's responsibilities..."
                      maxLength={MAX_DESCRIPTION_LENGTH}
                      rows={3}
                      className={cn(
                        descriptionLength > 0 &&
                          !isDescriptionValid &&
                          'border-destructive',
                      )}
                    />
                    {descriptionLength > 0 &&
                      descriptionLength < MIN_DESCRIPTION_LENGTH && (
                        <p className="text-xs text-destructive">
                          Description must be at least {MIN_DESCRIPTION_LENGTH}{' '}
                          characters
                        </p>
                      )}
                  </div>
                </div>
              </div>
              {/* Footer bar - fixed at bottom, outside scroll area */}
              <div className="shrink-0 border-t bg-background/95 backdrop-blur-sm px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {detailsModified && (
                      <span className="text-amber-600 dark:text-amber-400">
                        • Unsaved changes
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetDetails}
                      disabled={!detailsModified || isSavingDetails}
                    >
                      <ArrowCounterClockwiseIcon className="h-3.5 w-3.5 mr-1.5" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveDetails}
                      disabled={
                        !detailsModified || !isDetailsValid || isSavingDetails
                      }
                    >
                      {isSavingDetails ? (
                        <>
                          <CircleNotchIcon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FloppyDiskIcon className="h-3.5 w-3.5 mr-1.5" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members" className="h-full m-0 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Current Members */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">Current Members</h3>
                    <Badge variant="secondary">
                      {currentMembers.length} member
                      {currentMembers.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>

                  {groupDetailsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner className="h-6 w-6" />
                    </div>
                  ) : currentMembers.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-muted-foreground/25 bg-muted/30 p-6 text-center">
                      <UsersIcon className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No members in this team yet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1 rounded-lg border bg-card p-1">
                      {currentMembers.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50"
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-medium">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {member.name}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {member.email}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleRemoveMember(member.id)}
                            disabled={removingMemberId === member.id}
                          >
                            {removingMemberId === member.id ? (
                              <CircleNotchIcon className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <UserMinusIcon className="h-4 w-4 mr-1" />
                                Remove
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add Members */}
                <div>
                  <h3 className="font-medium mb-3">Add Members</h3>

                  {/* Search */}
                  <div className="relative mb-3">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users to add..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {allMembersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner className="h-6 w-6" />
                    </div>
                  ) : availableMembers.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-muted-foreground/25 bg-muted/30 p-6 text-center">
                      <UserPlusIcon className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {memberSearch
                          ? 'No matching users found'
                          : 'All users are already members of this team'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-[300px] overflow-y-auto rounded-lg border bg-card p-1">
                      {availableMembers.map((member) => (
                        <div
                          key={member.userId}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50"
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground text-sm font-medium">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {member.name}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {member.email}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => handleAddMember(member.userId)}
                            disabled={addingMemberId !== null}
                          >
                            {addingMemberId === member.userId ? (
                              <CircleNotchIcon className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <UserPlusIcon className="h-4 w-4 mr-1" />
                                Add
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default EditTeamModal;
